/**
 * 统一的 API 请求工具
 * 支持浏览器和 SSR 环境
 */

interface ApiRequestOptions extends RequestInit {
  data?: any;
  requestType?: 'json' | 'form';
  signal?: AbortSignal;
  /** SSR 环境下传递的 Cookie（从 pageContext.headers.cookie 获取） */
  cookie?: string;
  /** 响应类型：默认 'json'，'text' 模式跳过 JSON 解析和 Result 解包 */
  responseType?: 'json' | 'text';
}

const RESPONSE_PREVIEW_LIMIT = 1000;
const RESPONSE_FORMAT_ERROR_MESSAGE = '服务器响应异常，请稍后重试';

type ApiResultEnvelope = {
  success?: unknown;
  msg?: unknown;
  message?: unknown;
  code?: unknown;
  data?: unknown;
};

function getResponsePreview(text: string): string {
  return text.length > RESPONSE_PREVIEW_LIMIT
    ? `${text.slice(0, RESPONSE_PREVIEW_LIMIT)}...`
    : text;
}

function parseJsonWithLog<T>(
  text: string,
  context: { url: string; status: number; contentType: string | null }
): T {
  try {
    return JSON.parse(text) as T;
  } catch (error) {
    console.error('[apiRequest] 响应 JSON 解析失败:', {
      ...context,
      responseText: getResponsePreview(text),
      error,
    });
    throw new Error(RESPONSE_FORMAT_ERROR_MESSAGE);
  }
}

/**
 * 统一的 API 请求函数
 */
export default async function apiRequest<T = any>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  return directRequest<T>(path, options);
}

/**
 * 直接请求
 */
async function directRequest<T = any>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { data, requestType = 'json', responseType = 'json', signal, cookie, ...fetchOptions } = options;

  // 构建完整 URL（SSR 时需要完整 URL）
  const isSSR = typeof window === 'undefined';

  // 浏览器环境：根据路径添加代理前缀
  // - /public/* 路径：直接使用（Vite 代理保持原样）
  // - 其他路径：添加 /api 前缀（Vite 代理会去掉 /api）
  let url: string;
  if (isSSR) {
    // SSR 环境：使用完整 URL
    const baseUrl = process.env.VITE_API_TARGET || 'http://127.0.0.1:10030';
    url = `${baseUrl}${path}`;
  } else {
    // 浏览器环境：添加代理前缀
    url = path.startsWith('/public') ? path : `/api${path}`;
  }

  // 设置 headers
  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string>),
  };

  // 如果有数据需要发送
  if (data) {
    if (requestType === 'json') {
      headers['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(data);
    } else if (requestType === 'form') {
      // FormData 会自动设置 Content-Type
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      fetchOptions.body = formData;
    }
  }

  // 浏览器环境：从 localStorage 获取 token 和追踪码
  // Cookie 会自动发送，不需要手动设置
  if (!isSSR) {
    const token = localStorage.getItem('token');
    if (token) {
      headers['token'] = token;
    }
    const trackingCode = localStorage.getItem('trackingCode');
    if (trackingCode) {
      headers['X-Tracking-Code'] = trackingCode;
    }
  }

  // SSR 环境：手动传递 Cookie
  if (isSSR && cookie) {
    headers['Cookie'] = cookie;
  }

  // 构建最终的请求配置
  const finalOptions: RequestInit = {
    ...fetchOptions,
    headers,
    signal,
    // 浏览器环境需要 credentials: 'include' 才能自动发送和接收 Cookie
    credentials: isSSR ? 'same-origin' : 'include',
  };

  try {
    const response = await fetch(url, finalOptions);
    const contentType = response.headers.get('content-type');

    // 检查 HTTP 状态码
    if (!response.ok) {
      const errorText = await response.text().catch((error) => {
        console.error('[apiRequest] 读取错误响应失败:', {
          url,
          status: response.status,
          contentType,
          error,
        });
        return '';
      });
      const errorData = errorText
        ? parseJsonWithLog<{ msg?: string; message?: string; code?: string }>(errorText, {
          url,
          status: response.status,
          contentType,
        })
        : {};
      const error = new Error(errorData.msg || errorData.message || `请求失败: ${response.status}`) as Error & { code?: string };
      error.code = errorData.code;  // 保留错误码
      throw error;
    }

    // 处理空响应体
    const text = await response.text();
    if (!text || text.trim() === '') {
      return undefined as T;
    }

    // text 模式：直接返回原始文本
    if (responseType === 'text') {
      return text as T;
    }

    const result = parseJsonWithLog<ApiResultEnvelope>(text, {
      url,
      status: response.status,
      contentType,
    });

    // 如果返回的是 Result 包装格式，自动解包
    if (result && typeof result === 'object' && 'success' in result) {
      if (result.success === false) {
        const message = typeof result.msg === 'string'
          ? result.msg
          : typeof result.message === 'string'
            ? result.message
            : '请求失败';
        // 创建包含错误码的错误对象
        const error = new Error(message) as Error & { code?: string };
        if (typeof result.code === 'string') {
          error.code = result.code;
        }
        throw error;
      }
      return result.data as T;
    }

    return result as T;
  } catch (error: any) {
    // tokenError 自动清除 token 并跳转登录
    if (error.code === 'tokenError' && typeof window !== 'undefined') {
      localStorage.removeItem('token');

      // 如果当前已经在登录页或注册页，不再跳转，避免死循环
      const currentPath = window.location.pathname;
      if (currentPath === '/login' || currentPath === '/register') {
        throw error;
      }

      // 跳转到登录页，带上当前路径作为 redirect（不包含已有的 query string 避免累积）
      const returnUrl = encodeURIComponent(currentPath);
      window.location.href = `/login?redirect=${returnUrl}`;
      return new Promise(() => {});  // 阻止后续代码执行
    }
    // 保留错误码信息
    if (error.code) {
      throw error;
    }
    throw new Error(error.message || '请求失败');
  }
}

/**
 * 导出直接请求函数，用于特殊场景
 */
export { directRequest };
